// client/src/dnd/emitter_tool/hooks/overlay.js
import React from "react";
import { getShapeRenderer } from "./overlay/shapes/registry.js";
import { getOriginForMode } from "./overlay/modes/index.js";

function nowSec() {
  if (typeof performance !== "undefined" && performance.now) return performance.now() / 1000;
  return Date.now() / 1000;
}

function pulseScaleFromSettings(settings) {
  const s = settings || {};
  if (!s.pulseEnabled) return 1;
  const pingpong = !!s.pulsePingPong;
  const minPct = Math.max(0, Math.min(0.99, Number(s.pulseMinPct ?? 1)));
  const expandS = Math.max(1e-4, Number(s.pulseExpandTimeS ?? 1.5));
  const holdS = Math.max(0, Number(s.pulseHoldAtMaxS ?? 0));
  const delayS = Math.max(0, Number(s.pulseDelayAfterEmptyS ?? 0));
  const phaseOff = Number(s.pulsePhaseOffsetS ?? 0) || 0;
  const t = nowSec() - phaseOff;
  let k;
  if (pingpong) {
    const up = expandS, cyc = up * 2, p = ((t % cyc) + cyc) % cyc;
    k = p < up ? p / up : 1 - (p - up) / up;
  } else {
    const up = expandS, hold = holdS, wait = delayS, total = up + hold + wait || up;
    const p = ((t % total) + total) % total;
    k = p < up ? p / up : p < up + hold ? 1 : 0;
  }
  return Math.max(1e-6, minPct + (1 - minPct) * k);
}

function applyPulseToDims(shape, settings, ppm) {
  const k = pulseScaleFromSettings(settings);
  const s = { ...(settings || {}) };
  const key = String(shape || "").toLowerCase();
  if (key === "circle" || key === "arc" || key === "cone") {
    const r = Number(s.radius ?? 1);
    s.radius = r;
    s._radiusPx = r * ppm * k;
  }
  if (key === "square" || key === "triangle" || key === "line") {
    const w = Number(s.width ?? 2);
    const h = Number(s.height ?? (key === "line" ? 0 : 2));
    s.width = w;
    s.height = h;
    s._widthPx = w * ppm * k;
    s._heightPx = h * ppm * k;
  }
  s.__pulseScaleOverlay = k;
  return s;
}

export function drawShapeOverlay(
  g,
  app,
  {
    show,
    shape,
    settings = {},
    origin,
    center,
    pxPerMeter,
  }
) {
  const fallback = {
    x: app?.renderer?.width ? app.renderer.width / 2 : 0,
    y: app?.renderer?.height ? app.renderer.height / 2 : 0,
  };

  const start = (typeof origin === "function" ? origin() : origin) || fallback;
  const end   = (typeof center  === "function" ? center()  : center)  || start;

  try { g.clear(); } catch {}

  const dbg = settings?.debug || {};
  const showCrosshair = !!dbg.crosshair;
  const showGuide     = !!dbg.guide;

  if (showCrosshair) {
    try { g.lineStyle({ width: 1, color: 0x00ffff, alpha: 0.9 }); }
    catch { g.lineStyle(1, 0x00ffff, 0.9); }
    g.moveTo(start.x - 4, start.y); g.lineTo(start.x + 4, start.y);
    g.moveTo(start.x, start.y - 4); g.lineTo(start.x, start.y + 4);
    g.drawCircle(start.x, start.y, 2);
  }

  if (showGuide && (end && (end.x !== start.x || end.y !== start.y))) {
    try { g.lineStyle({ width: 1, color: 0x00ffff, alpha: 0.5 }); }
    catch { g.lineStyle(1, 0x00ffff, 0.5); }
    g.moveTo(start.x, start.y);
    g.lineTo(end.x, end.y);
  }

  if (!show) return;

  const s = { ...(settings || {}) };
  if (Number.isFinite(s.angleDeg) && !Number.isFinite(s.rotationDeg)) s.rotationDeg = s.angleDeg;
  if (Number.isFinite(s.rotationDeg) && !Number.isFinite(s.angleDeg)) s.angleDeg = s.rotationDeg;

  const ppm = Number.isFinite(pxPerMeter) && pxPerMeter > 0 ? pxPerMeter : 80;

  const walls =
    (typeof window !== "undefined" &&
      Array.isArray(window.__EmitterWallsLast?.segments))
      ? window.__EmitterWallsLast.segments
      : [];

  const renderer = getShapeRenderer(shape);
  const pulsed = applyPulseToDims(shape, s, ppm);

  const centerOn = String(s.centerOn || "").toLowerCase();
  const anchor = (centerOn === "token" || centerOn === "origin") ? start : end;

  renderer(g, app, {
    origin: anchor,
    settings: { ...pulsed, pxPerMeter: ppm },
    pxPerMeter: ppm,
    show: !!show,
    walls,
  });
}

function coerceVectorPair(maybe, inputs) {
  const m = maybe || {};
  if (Number.isFinite(m.x) && Number.isFinite(m.y)) {
    return { start: m, end: m };
  }
  const start =
    m.origin || m.start || m.from || inputs?.origin || inputs?.start || inputs?.from || null;
  const end =
    m.target || m.end || m.to || inputs?.target || inputs?.end || inputs?.to || start || null;
  return { start, end };
}

export function drawOverlayWithMode(
  g,
  app,
  {
    show,
    shape,
    settings = {},
    mode,
    inputs = {},
    pxPerMeter,
  }
) {
  const res = getOriginForMode(mode, app, inputs);
  const { start, end } = coerceVectorPair(res, inputs);
  const useStart = start || end || { x: app?.renderer?.width / 2 || 0, y: app?.renderer?.height / 2 || 0 };
  const useEnd   = end   || start || useStart;
  return drawShapeOverlay(g, app, {
    show,
    shape,
    settings,
    origin: useStart,
    center: useEnd,
    pxPerMeter,
  });
}

export function installEmitterOverlay({ app, graphics, getOrigin, getPxPerMeter }) {
  if (!app || !graphics) throw new Error("installEmitterOverlay: app + graphics required");
  if (typeof getOrigin !== "function") getOrigin = () => ({ x: 0, y: 0 });

  try {
    if (!app.stage.sortableChildren) app.stage.sortableChildren = true;
    if (!graphics.parent) app.stage.addChild(graphics);
    graphics.zIndex = 99999;
    graphics.visible = true;
    graphics.renderable = true;
    graphics.alpha = 1;
    app.stage.sortChildren?.();
  } catch {}

  let cfg = { show: false, shape: "dot", settings: {} };
  let gotConfig = false;

  const originProvider = () => {
    try {
      const o = getOrigin();
      if (o && Number.isFinite(o.x) && Number.isFinite(o.y)) return o;
    } catch {}
    return { x: app?.renderer?.width / 2 || 0, y: app?.renderer?.height / 2 || 0 };
  };

  const paint = () => {
    const ppm = (() => {
      try {
        const v = getPxPerMeter?.();
        if (Number.isFinite(v) && v > 0) return v;
      } catch {}
      return 80;
    })();

    drawShapeOverlay(graphics, app, {
      show: gotConfig ? !!cfg.show : false,
      shape: gotConfig ? cfg.shape : "dot",
      settings: gotConfig ? { ...(cfg.settings || {}), centerOn: "token" } : { centerOn: "token" },
      origin: originProvider,
      center: originProvider,
      pxPerMeter: ppm,
    });
  };

  const onCfg = (e) => {
    const d = e?.detail || {};
    cfg = {
      show: !!d.show,
      shape: String(d.shape || cfg.shape || "dot"),
      settings: { ...(cfg.settings || {}), ...(d.settings || {}) },
    };
    gotConfig = true;
  };
  window.addEventListener("EmitterTool:UI:EmitterOverlayConfig", onCfg);

  try {
    window.dispatchEvent(new CustomEvent("EmitterTool:UI:RequestEmitterOverlayConfig"));
  } catch {}

  const tick = () => {
    if (!graphics.destroyed) paint();
  };
  app.ticker.add(tick);

  const onResize = () => paint();
  window.addEventListener("resize", onResize);

  return {
    refresh: paint,
    destroy() {
      window.removeEventListener("EmitterTool:UI:EmitterOverlayConfig", onCfg);
      window.removeEventListener("resize", onResize);
      try { app.ticker.remove(tick); } catch {}
      try { graphics.clear(); } catch {}
    },
  };
}

export function useEmitterOverlay({ app, graphics, getOrigin, getPxPerMeter }) {
  React.useEffect(() => {
    if (!app || !graphics) return;
    const api = installEmitterOverlay({ app, graphics, getOrigin, getPxPerMeter });
    return () => { try { api.destroy(); } catch {} };
  }, [app, graphics, getOrigin, getPxPerMeter]);
}
